package top.wcpe.allincore.ui.runtime.lifecycle

import top.wcpe.allincore.ui.runtime.element.UIElement
import kotlin.jvm.JvmStatic

/**
 * 元素树 lifecycle 操作工具集。
 *
 * RFC-RECOMPOSITION：抽出原 RenderRoot 内的递归 dispose 算法为公共工具，
 * 让数据驱动容器（M6）、VirtualList、conditional 容器在淘汰子元素时统一走完整的
 * 资源释放路径，避免子树内的 Effect / Animation / FSM 残留订阅。
 */
object LifecycleUtils {

    /**
     * 递归释放元素子树的 [LifecycleScope]。
     *
     * 顺序：先递归子元素，再释放自身（与对象构造顺序逆序，符合栈式释放语义）。
     * 每个元素的 `lifecycleScope().dispose()` 是幂等的，重复调用安全。
     *
     * 本方法**不会**修改父元素的 children 列表；调用方需要自行从父 children
     * 中移除（如通过 `parent.replaceChildren(newList)` 替换为不含 stale 的列表）。
     *
     * @param element 要递归释放的根元素；为 `null` 时直接返回
     */
    @JvmStatic
    fun disposeRecursively(element: UIElement<*>?) {
        if (element == null) return
        for (child in element.children()) {
            disposeRecursively(child)
        }
        element.lifecycleScope().dispose()
    }

    /**
     * 递归暂停元素子树的 [LifecycleScope]。
     *
     * 顺序：先递归子元素，再暂停自身（与 disposeRecursively 一致的后序遍历）。
     * 暂停不会清空 Disposable 列表，仅调用每个 [Pausable] 的 `pause()`。
     *
     * @param element 要递归暂停的根元素；为 `null` 时直接返回
     */
    @JvmStatic
    fun suspendRecursively(element: UIElement<*>?) {
        if (element == null) return
        for (child in element.children()) {
            suspendRecursively(child)
        }
        element.lifecycleScope().suspend()
    }

    /**
     * 递归恢复元素子树的 [LifecycleScope]。
     *
     * 顺序：先恢复自身，再递归子元素（前序遍历，与 suspend 逆序）。
     * 父元素的 Effect 先重建依赖，子元素的 Effect 后重建，
     * 确保子 Effect 读取父级状态时拿到最新值。
     *
     * @param element 要递归恢复的根元素；为 `null` 时直接返回
     */
    @JvmStatic
    fun resumeRecursively(element: UIElement<*>?) {
        if (element == null) return
        element.lifecycleScope().resume()
        for (child in element.children()) {
            resumeRecursively(child)
        }
    }
}
